import { Injectable, signal } from '@angular/core';
import jsPDF from 'jspdf';
import { PassaggioConsegneResponse } from '../models/passaggio-consegne.model';


@Injectable()
export class PassaggioConsegneModuliBuilderService {

  private readonly data = signal<PassaggioConsegneResponse|undefined>(undefined);
  private readonly isTipoVerificaCampione = signal<boolean>(false);
  private readonly progressiviDocumenti = signal<string[]>([]);

  private readonly filename = signal<string>('passaggio_consegne.pdf');

  constructor() { }

  public generatePDF(): void {

    setTimeout(() => {
      const doc = new jsPDF('l', 'mm', 'a3');
      const totalPagesExp = '{total_pages_count_string}';

      this.printHeader(doc, PASSAGGIO_CONSEGNE_CONSTANT.MARGIN.TOP);
      this.printBody(doc, PASSAGGIO_CONSEGNE_CONSTANT.MARGIN.TOP + 90);
      this.printFooter(doc);

      if (typeof doc.putTotalPages === 'function') {
        doc.putTotalPages(totalPagesExp);
      }
      doc.save(this.filename());
    }, 100);
  }

  public setFileName( filename: string) : void 
  {
    this.filename.set(filename);
  }

  public setData( tipoVerifica: string | null | undefined, progressivi: string | null | undefined, data: PassaggioConsegneResponse | undefined) : void 
  {
    this.data.set(data);

    if (tipoVerifica)
      this.isTipoVerificaCampione.set( (tipoVerifica === 'campione'));

    if ( progressivi)
      this.progressiviDocumenti.set(progressivi.split(","));
  }

  private printHeader(doc:jsPDF, startY:number): void {

    this.printToday(doc);
    this.printLogo( doc, startY);
    this.printTitle( doc, startY + 45)
     
    this.printPageNumber(doc);
  }

  private printFooter(doc:jsPDF): void {

    const footerY = doc.internal.pageSize.getHeight() - 30;
    const roleY = footerY;
    const signY = footerY + 15;
    const nameY = footerY + 20;

    this.setFont(doc);

    doc.text("Funzionario uscente", (doc.internal.pageSize.width/4), roleY, {align: 'center'});
    doc.line((doc.internal.pageSize.width/6), signY, (doc.internal.pageSize.width/6)*2, signY);

    doc.text("Funzionario subentrante", (doc.internal.pageSize.width/4)*3, roleY, {align: 'center'});
    doc.line((doc.internal.pageSize.width/6)*4, signY, (doc.internal.pageSize.width/6)*5, signY);

    this.setFont(doc, PASSAGGIO_CONSEGNE_CONSTANT.FONT.STYLE.NORMAL, PASSAGGIO_CONSEGNE_CONSTANT.FONT.SIZE.SMALL );

    doc.text(`(${this.data()?.funzionarioUscente})`, (doc.internal.pageSize.width/4), nameY, {align: 'center'})
    doc.text(`(${this.data()?.funzionarioSubentrante})`, (doc.internal.pageSize.width/4)*3, nameY, {align: 'center'})
 
    this.printPageNumber(doc); 
  }

  private printLogo(doc:jsPDF, startY:number): void {

    const middle = (doc.internal.pageSize.width - PASSAGGIO_CONSEGNE_CONSTANT.LOGO.SIZE) / 2;

    doc.addImage(
      PASSAGGIO_CONSEGNE_CONSTANT.LOGO.PATH,PASSAGGIO_CONSEGNE_CONSTANT.LOGO.FORMAT
        ,middle, startY
        ,PASSAGGIO_CONSEGNE_CONSTANT.LOGO.SIZE, PASSAGGIO_CONSEGNE_CONSTANT.LOGO.SIZE);
  }

  private printTitle( doc: jsPDF, startY: number): void {

    const middle = doc.internal.pageSize.width / 2; 

    const xLineFrom = (doc.internal.pageSize.width/6)*2; 
    const xLineTo = (doc.internal.pageSize.width/6)*4;

    doc.setLineWidth(0.5);
    doc.line(xLineFrom, startY - 10, xLineTo, startY - 10);

    this.setFont(doc, PASSAGGIO_CONSEGNE_CONSTANT.FONT.STYLE.ITALIC, PASSAGGIO_CONSEGNE_CONSTANT.FONT.SIZE.LARGE)
    doc.text('(TIMBRO LINEARE D\'UFFICIO)', middle, startY, { align: 'center' });
    doc.text('ORGANO PERIFERICO DI SICUREZZA', middle, startY + 10, { align: 'center' });
   
    this.setFont(doc, PASSAGGIO_CONSEGNE_CONSTANT.FONT.STYLE.BOLD, PASSAGGIO_CONSEGNE_CONSTANT.FONT.SIZE.LARGE)
    doc.text('MODULO PASSAGGIO DI CONSEGNE DEI DOCUMENTI CLASSIFICATI', middle, startY + 30, { align: 'center' });
  }

  private printBody(doc: jsPDF, startY: number) : void
  {
    const x = PASSAGGIO_CONSEGNE_CONSTANT.MARGIN.LEFT;
    const BODY_WIDTH = doc.internal.pageSize.getWidth() - (x*2)
   
    const dataPassaggio = this.formatDate(this.data()?.dataPassaggioConsegne);

    this.setFont(doc);

    if(!this.isTipoVerificaCampione()){
      const message = `Si dichiara che alla data del ${dataPassaggio}, il carico e la completezza dei documenti classificati risultanti dal presente registro sono stati riscontrati esatti.`;
      doc.text(message, x, startY, { maxWidth: BODY_WIDTH });
    } else {
      let startProgressiviY = startY + 10;
      let page = 1;
      let currentItemInPage = 0;

      let message = `Si dichiara che in data ${dataPassaggio} è stata verificata a campione la completezza dei documenti contraddistinti con i numeri progressivi:`;
      doc.text(message, x, startY, { maxWidth: BODY_WIDTH });

      this.setFont(doc,PASSAGGIO_CONSEGNE_CONSTANT.FONT.STYLE.BOLD);
      this.progressiviDocumenti()
        .map( e=> e.trim())
        .filter( e=> e !== '')
        .forEach((e:string, i:number) => {

        if ( currentItemInPage > PASSAGGIO_CONSEGNE_CONSTANT.PAGE.NUM_PROGRESSIVI_PER_PAGE) {
            doc.addPage();
            page += 1;
            currentItemInPage=0;
            startProgressiviY = (PASSAGGIO_CONSEGNE_CONSTANT.MARGIN.TOP+10);
        }
        doc.text(`• ${e}`, x + 5, startProgressiviY + (currentItemInPage++)*10, { maxWidth: BODY_WIDTH });
      });

      this.setFont(doc);
      doc.text('Risultati tutti tenuti in modo corretto.',x, startProgressiviY + (currentItemInPage)*10);
    }
  }

  private printToday(doc:jsPDF): void 
  {
    const text = new Date().toLocaleDateString('it-IT');
    this.setFont(doc, PASSAGGIO_CONSEGNE_CONSTANT.FONT.STYLE.NORMAL, PASSAGGIO_CONSEGNE_CONSTANT.FONT.SIZE.XSMALL)
    doc.text(text, PASSAGGIO_CONSEGNE_CONSTANT.MARGIN.LEFT, PASSAGGIO_CONSEGNE_CONSTANT.MARGIN.TOP);
  }

  private printPageNumber(doc: jsPDF): void 
  {
    const totalPagesExp = '{total_pages_count_string}';
    const pageHeight =  doc.internal.pageSize.height ?  doc.internal.pageSize.height :  doc.internal.pageSize.getHeight();

    const x = PASSAGGIO_CONSEGNE_CONSTANT.MARGIN.LEFT + 5; 
    const y = pageHeight - PASSAGGIO_CONSEGNE_CONSTANT.MARGIN.BOTTOM;

    let text = `Pagina ${doc.getNumberOfPages()}`;
    if (typeof doc.putTotalPages === 'function') {
      text = `${text} di ${totalPagesExp}`;
    }
    
    this.setFont(doc, PASSAGGIO_CONSEGNE_CONSTANT.FONT.STYLE.NORMAL, PASSAGGIO_CONSEGNE_CONSTANT.FONT.SIZE.XSMALL)
    doc.text(text, x, y);
  }

  private setFont( doc: jsPDF, style: string = PASSAGGIO_CONSEGNE_CONSTANT.FONT.STYLE.NORMAL, size: number = PASSAGGIO_CONSEGNE_CONSTANT.FONT.SIZE.DEFAULT) : void 
  {
    doc.setFont(PASSAGGIO_CONSEGNE_CONSTANT.FONT.NAME, style);
    doc.setFontSize(size);
  }

  private formatDate( value : Date| null| undefined) {

    const EMPTY_MASK = '--/--/---- --:--';
    const valueChecked : Date | undefined = (value)?new Date(value):undefined;
    
    return ( valueChecked)?`${valueChecked.toLocaleDateString()} ${valueChecked.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}`:EMPTY_MASK;
  }
}

export const PASSAGGIO_CONSEGNE_CONSTANT = {
  FONT : {
    NAME: 'Helvetica',
    STYLE : { 
      NORMAL : 'normal',
      ITALIC : 'italic',
      BOLD : 'bold',     
    },
    SIZE : {
      LARGE: 16,
      DEFAULT: 14,
      SMALL: 10,
      XSMALL: 8,
    }
  },
  MARGIN: {
    TOP : 5,
    BOTTOM : 5,
    RIGHT : 20,
    LEFT : 20,
  },
  LOGO : {
    PATH : '/assets/images/italia-emblema.png',
    FORMAT : 'PNG',
    SIZE : 20,
  },
  PAGE: {
    NUM_PROGRESSIVI_PER_PAGE : 15
  }
} 